/**
 * SystemService
 * Service for managing system services
 * Route: api/system
 */
import { BaseService } from '../base/BaseService';
/**
 * SystemService
 * Handles CRUD operations for system service registry
 */
export class SystemService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? SystemService.DEFAULT_SERVICE_PATH,
            basePath: SystemService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL SERVICES ====================
    /**
     * Get all system services with pagination
     * GET api/system?page={page}&pageSize={pageSize}
     */
    async getAllServices(page = 1, pageSize = 50) {
        const response = await this.getAllServicesRaw(page, pageSize);
        return response.data ?? [];
    }
    /**
     * Get all system services with full response
     */
    getAllServicesRaw(page = 1, pageSize = 50) {
        const endpoint = this.getEndpoint('', '');
        const url = this.buildUrlWithParams(endpoint, { page: page.toString(), pageSize: pageSize.toString() });
        return this.http.get(url);
    }
    // ==================== GET ACTIVE SERVICES ====================
    /**
     * Get active system services
     * GET api/system/active
     */
    async getActiveServices() {
        const response = await this.getActiveServicesRaw();
        return response.data ?? [];
    }
    /**
     * Get active system services with full response
     */
    getActiveServicesRaw() {
        const endpoint = this.getEndpoint('active', 'active');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET SERVICE BY ID ====================
    /**
     * Get system service by ID
     * GET api/system/{id}
     */
    async getServiceById(id) {
        const response = await this.getServiceByIdRaw(id);
        if (!response.data) {
            throw new Error(`System service ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get system service by ID with full response
     */
    getServiceByIdRaw(id) {
        const endpoint = this.getEndpoint('by-id', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== CREATE SERVICE ====================
    /**
     * Create a new system service
     * POST api/system
     */
    async createService(model) {
        const response = await this.createServiceRaw(model);
        if (!response.data) {
            throw new Error('Failed to create system service');
        }
        return response.data;
    }
    /**
     * Create a new system service with full response
     */
    createServiceRaw(model) {
        const endpoint = this.getEndpoint('create', '');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, model);
    }
    // ==================== UPDATE SERVICE ====================
    /**
     * Update an existing system service
     * PUT api/system/{id}
     */
    async updateService(id, model) {
        const response = await this.updateServiceRaw(id, model);
        if (!response.data) {
            throw new Error(`Failed to update system service ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing system service with full response
     */
    updateServiceRaw(id, model) {
        const endpoint = this.getEndpoint('update', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, model);
    }
    // ==================== DELETE SERVICE ====================
    /**
     * Delete a system service
     * DELETE api/system/{id}
     */
    async deleteService(id) {
        const response = await this.deleteServiceRaw(id);
        return response.success;
    }
    /**
     * Delete a system service with full response
     */
    deleteServiceRaw(id) {
        const endpoint = this.getEndpoint('delete', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
}
SystemService.DEFAULT_SERVICE_PATH = 'system';
SystemService.BASE_PATH = 'api/system';
//# sourceMappingURL=SystemService.js.map